import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";

export const SMSAutoReplySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [message, setMessage] = useState('');
  const [sendCondition, setSendCondition] = useState('always');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('sms_auto_reply_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setIsEnabled(data.is_enabled || false);
        setMessage(data.auto_reply_message || '');
        setSendCondition(data.send_condition || 'always');
      }
    } catch (error) {
      console.error('Error fetching auto-reply settings:', error);
      toast.error('Failed to load auto-reply settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (isEnabled && !message.trim()) {
      toast.error('Please enter an auto-reply message');
      return;
    }

    if (message.length > 1600) {
      toast.error('Message is too long (max 1600 characters)');
      return;
    }

    setSaving(true);

    try {
      // Check if settings exist
      const { data: existing } = await supabase
        .from('sms_auto_reply_settings')
        .select('id')
        .limit(1)
        .maybeSingle();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from('sms_auto_reply_settings')
          .update({
            is_enabled: isEnabled,
            auto_reply_message: message,
            send_condition: sendCondition,
          })
          .eq('id', existing.id);

        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from('sms_auto_reply_settings')
          .insert({
            is_enabled: isEnabled,
            auto_reply_message: message,
            send_condition: sendCondition,
          });

        if (error) throw error;
      }

      toast.success('Auto-reply settings saved successfully');
    } catch (error) {
      console.error('Error saving auto-reply settings:', error);
      toast.error('Failed to save auto-reply settings');
    } finally {
      setSaving(false);
    }
  };

  const calculateSMSSegments = (text: string) => {
    if (text.length === 0) return 0;
    return Math.ceil(text.length / 160);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">Loading auto-reply settings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Auto-Reply Settings</CardTitle>
        <CardDescription>
          Configure automatic SMS responses when customers text you
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="auto-reply-enabled">Enable Auto-Reply</Label>
            <p className="text-sm text-muted-foreground">
              Automatically respond to incoming customer messages
            </p>
          </div>
          <Switch
            id="auto-reply-enabled"
            checked={isEnabled}
            onCheckedChange={setIsEnabled}
          />
        </div>

        {/* Send Condition Dropdown */}
        <div className="space-y-2">
          <Label htmlFor="send-condition">When to Send</Label>
          <Select value={sendCondition} onValueChange={setSendCondition}>
            <SelectTrigger id="send-condition">
              <SelectValue placeholder="Select when to send auto-reply" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="always">Always (every message)</SelectItem>
              <SelectItem value="after_hours">After Hours Only</SelectItem>
              <SelectItem value="no_agents_online">When No Agents Online</SelectItem>
              <SelectItem value="never">Never (disabled)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {sendCondition === 'always' && 'Send auto-reply to every incoming message'}
            {sendCondition === 'after_hours' && 'Only send auto-reply outside business hours'}
            {sendCondition === 'no_agents_online' && 'Only send when no agents are available'}
            {sendCondition === 'never' && 'Auto-reply is disabled'}
          </p>
        </div>

        {/* Message Textarea */}
        <div className="space-y-2">
          <Label htmlFor="auto-reply-message">Auto-Reply Message</Label>
          <Textarea
            id="auto-reply-message"
            placeholder="Thank you for contacting us! An agent will respond shortly."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="resize-none"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>
              {message.length} / 1600 characters ({calculateSMSSegments(message)} SMS segment{calculateSMSSegments(message) !== 1 ? 's' : ''})
            </span>
            {message.length > 1600 && (
              <span className="text-destructive font-medium">Message too long!</span>
            )}
          </div>
        </div>

        {/* Preview Section */}
        {message.trim() && (
          <div className="space-y-2">
            <Label>Preview</Label>
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="h-4 w-4 text-primary-foreground" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium mb-1">Your Business</p>
                  <div className="bg-card rounded-lg p-3 shadow-sm">
                    <p className="text-sm">{message}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};